import React, { createContext, useContext, useState } from 'react';
import * as R from 'ramda';
import Result from '../common/result';
import {
  EmailError,
  emailFormatValidationError,
  emailGenericError,
  emailNetworkError,
  EmailResult,
  emailRetriesExhaustedError,
  emailTokenVericationError,
} from './email/emailCapture';
import validationService from '../services/validation';
import { Step, ValidationStep, ValidationStepStatus } from '../services/validation/validationService';
import { ValidationContext } from './ValidationContext';
import { moveItemToFront } from '../utils/utils';

export interface EmailValidationProps {
  email: string;
  setEmail(email: string): void;
  token: string;
  setToken(token: string): void;
  submitEmail(): Promise<EmailResult>;
  verifyEmail(): Promise<EmailResult>;
}

export const EmailValidationContext = createContext<EmailValidationProps>({} as EmailValidationProps);

type EmailValidationProviderProps = {
  children: JSX.Element;
};

/**
 *
 * @param props
 * @constructor
 */
export function EmailValidationProvider(props: EmailValidationProviderProps): JSX.Element {
  const [email, setEmail] = useState<string>('');
  const [token, setToken] = useState<string>('');
  const { validationId, updateValidationStatus } = useContext(ValidationContext);
  const { children } = props;

  async function mapEmailResponse(stepResult: Step): Promise<EmailResult> {
    let errorCodes = stepResult.errorCodes || [];

    const responseMapping: Record<string, Result<EmailError>> = {
      'step.no.retries': Result.failed(emailRetriesExhaustedError()),
      'email.token.mismatch': Result.failed(emailTokenVericationError()),
      'email.invalid': Result.failed(emailFormatValidationError()),
    };

    let result: EmailResult | null = null;
    // Move no retries to the front of the error code list so it gets priority
    if (stepResult.status === ValidationStepStatus.invalid && !R.isEmpty(errorCodes)) {
      errorCodes = moveItemToFront(errorCodes, 'step.no.retries');
      await updateValidationStatus();
      const code = R.head(errorCodes) || '';
      result = responseMapping[code] || Result.failed(emailGenericError());
    } else {
      result = Result.noValueSuccess();
    }

    return result;
  }

  async function submitEmail(): Promise<EmailResult> {
    if (!validationId) {
      return Result.noValueFailed(emailGenericError());
    }

    const result = await validationService.processStep({
      step: ValidationStep.email,
      validationId,
      value: {
        email,
      },
    });

    if (!result.isSuccessful()) {
      result.logError('EmailValidationProvider: Error processing email step');
      await updateValidationStatus();
      return Result.noValueFailed(emailNetworkError(result.failure()));
    }
    const stepResult = result.success();
    return mapEmailResponse(stepResult);
  }

  async function verifyEmail(): Promise<EmailResult> {
    if (!validationId) {
      return Result.noValueFailed(emailGenericError());
    }

    const result = await validationService.processStep({
      step: ValidationStep.emailToken,
      validationId,
      value: {
        token,
      },
    });

    if (!result.isSuccessful()) {
      result.logError('EmailValidationProvider: Error processing email token step');
      await updateValidationStatus();
      return Result.noValueFailed(emailNetworkError(result.failure()));
    }

    const stepResult = result.success();
    return mapEmailResponse(stepResult);
  }

  return (
    <EmailValidationContext.Provider
      value={{
        email,
        setEmail,
        token,
        setToken,
        submitEmail,
        verifyEmail,
      }}
    >
      {children}
    </EmailValidationContext.Provider>
  );
}
